import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import useCtx from "./hooks/useCts";
import { signOut } from "./firebase";
import HomeScreen from "./screens/HomeScreen";
import LoginScreen from "./screens/LoginScreen";
import BooksPDFScreen from "./screens/BooksPDFScreen";
import LoyaltyProgramScreen from "./screens/LoyaltyProgramScreen";
import OrderScreen from "./screens/OrderScreen";
import ManagmentScreen from "./screens/ManagmentScreen";
import { findClientForAdmin, findClientForTeacher } from "./utils/clientAccess";
import {
  trackScreenViewStart,
  trackScreenViewEnd,
} from "./utils/adminTracking";

const RouteAnalytics: React.FC = () => {
  const ctx = useCtx();
  const { pathname } = useLocation();

  // mapujemy polskie ścieżki na nazwy ekranów
  const pathToScreen = (path: string): AdminScreen => {
    if (ctx.isTeacher) {
      if (path === "/") return "BookPDFScreen";
    }
    if (path === "/" || path.startsWith("/home")) return "HomeScreen";
    if (path.startsWith("/podreczniki")) return "BookPDFScreen";
    if (path.startsWith("/zamowienia")) return "OrderScreen";
    if (path.startsWith("/program-lojalnosciowy"))
      return "LoyaltyProgramScreen";
    if (path.startsWith("/zarzadzanie")) return "ManagmentScreen";
    return "HomeScreen";
  };

  // RouteAnalytics.tsx
  React.useEffect(() => {
    if (!ctx.user) return;

    // dopóki nie ma dopasowanego klienta (po authId/email) — nic nie loguj
    const currentClient =
      findClientForAdmin(ctx.clients ?? [], ctx.user.uid, ctx.user.email) ??
      findClientForTeacher(ctx.clients ?? [], ctx.user.email) ??
      null;
    if (!currentClient) return;

    const screen = pathToScreen(pathname);

    // Wyciągamy bookId z path jeśli jest: /podreczniki/{bookId}
    const bookIdFromPath = (() => {
      if (!pathname.startsWith("/podreczniki")) return undefined;
      const parts = pathname.split("/").filter(Boolean); // ["podreczniki", "{id}?"]
      return parts[1] || undefined;
    })();

    trackScreenViewStart(ctx, screen, pathname, {
      role: ctx.isTeacher ? "teacher" : "admin",
      ...(bookIdFromPath ? { bookId: bookIdFromPath } : {}),
    });
    return () => {
      trackScreenViewEnd(ctx, screen, pathname);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, !!ctx.user, ctx.clients?.length, ctx.isTeacher]);

  return null;
};

const LoadingScreen: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#f7f2ea] px-4">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#f6851f]" />
  </div>
);

const AccessDeniedScreen: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#f7f2ea] px-4 text-center">
    <div className="max-w-md rounded-3xl border border-white/70 bg-white/70 p-8 shadow-[0_20px_60px_rgba(148,163,184,0.2)]">
      <h1 className="text-2xl font-semibold text-slate-900">
        Brak dostępu do panelu
      </h1>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        To konto nie jest przypisane do szkoły jako admin ani lektor.
      </p>
      <button
        type="button"
        onClick={() => void signOut()}
        className="mt-6 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
      >
        Wyloguj
      </button>
    </div>
  </div>
);

export default function App() {
  const ctx = useCtx();
  const isResolvingAccess =
    !ctx.authLoaded || Boolean(ctx.user && !ctx.isRoleResolved);

  return (
    <Router>
      {isResolvingAccess && <LoadingScreen />}

      {!isResolvingAccess && ctx.user && ctx.currentClient && (
        <RouteAnalytics />
      )}

      {!isResolvingAccess && !ctx.user && (
        <Routes>
          <Route path="/" element={<LoginScreen />} />
          <Route path="/*" element={<Navigate to="/" replace />} />
        </Routes>
      )}

      {!isResolvingAccess && ctx.user && !ctx.currentClient && (
        <Routes>
          <Route path="/*" element={<AccessDeniedScreen />} />
        </Routes>
      )}

      {!isResolvingAccess && ctx.user && ctx.currentClient && !ctx.isTeacher && (
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/podreczniki" element={<BooksPDFScreen />} />
          <Route path="/podreczniki/:bookId" element={<BooksPDFScreen />} />
          <Route path="/zamowienia" element={<OrderScreen />} />
          <Route
            path="/program-lojalnosciowy"
            element={<LoyaltyProgramScreen />}
          />
          <Route path="/zarzadzanie" element={<ManagmentScreen />} />
          {/* <Route path="/klienci" element={<ClientScreen />} /> */}
          {/* <Route path="/statystyki" element={<ChartsScreen />} />
            <Route path="/produkcja" element={<ProductionScreen />} /> */}
          <Route path="/*" element={<Navigate to="/" replace />} />
        </Routes>
      )}

      {!isResolvingAccess && ctx.user && ctx.currentClient && ctx.isTeacher && (
        <Routes>
          <Route path="/" element={<BooksPDFScreen />} />
          <Route path="/podreczniki" element={<BooksPDFScreen />} />
          <Route path="/podreczniki/:bookId" element={<BooksPDFScreen />} />
          <Route path="/*" element={<Navigate to="/podreczniki" replace />} />
        </Routes>
      )}
    </Router>
  );
}
